import { useEffect, useRef, useCallback } from 'react';
import { io, Socket } from 'socket.io-client';

const SOCKET_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

interface UseVocabGameSocketOptions {
  onConnect?: () => void;
  onDisconnect?: () => void;
  onError?: (msg: string) => void;
  // Teacher events
  onRoomCreated?: (data: { code: string; totalQuestions: number; setName: string }) => void;
  onTeamJoined?: (data: { teams: any[] }) => void;
  onGameStarted?: (data: { totalQuestions: number; teams: any[] }) => void;
  onStatsUpdate?: (data: { teams: any[] }) => void;
  onAnswerProgress?: (data: any) => void;
  // Student events
  onJoinSuccess?: (data: { teamName: string; roomCode: string; setName: string; totalQuestions: number }) => void;
  onStudentGameStarted?: (data: { questions: any[]; totalQuestions: number; setName: string }) => void;
  onTeamResult?: (data: any) => void;
  // Shared
  onFinalResult?: (data: any) => void;
}

export function useVocabGameSocket(options: UseVocabGameSocketOptions) {
  const socketRef = useRef<Socket | null>(null);
  const optionsRef = useRef(options);
  optionsRef.current = options;

  useEffect(() => {
    const socket = io(SOCKET_URL, {
      transports: ['websocket', 'polling'],
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      console.log('[VocabGame] Socket connected:', socket.id);
      optionsRef.current.onConnect?.();
    });

    socket.on('disconnect', () => {
      console.log('[VocabGame] Socket disconnected');
      optionsRef.current.onDisconnect?.();
    });

    socket.on('error', (data: { message: string }) => {
      console.error('[VocabGame] Socket error:', data.message);
      optionsRef.current.onError?.(data.message);
    });

    // Teacher events
    socket.on('room:created', (data) => optionsRef.current.onRoomCreated?.(data));
    socket.on('room:team_joined', (data) => optionsRef.current.onTeamJoined?.(data));
    socket.on('room:stats_update', (data) => optionsRef.current.onStatsUpdate?.(data));
    socket.on('room:answer_progress', (data) => optionsRef.current.onAnswerProgress?.(data));

    // game:started — different payload for teacher vs student
    socket.on('game:started', (data) => {
      if (data.questions) {
        // Student payload
        optionsRef.current.onStudentGameStarted?.(data);
      } else {
        // Teacher payload
        optionsRef.current.onGameStarted?.(data);
      }
    });

    // Student events
    socket.on('team:join_success', (data) => optionsRef.current.onJoinSuccess?.(data));
    socket.on('team:result', (data) => optionsRef.current.onTeamResult?.(data));

    // Shared
    socket.on('game:final_result', (data) => optionsRef.current.onFinalResult?.(data));

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, []); // only once

  // Emit helpers
  const createRoom = useCallback((data: { setId: number; setName: string; questionLimit: number; token: string }) => {
    socketRef.current?.emit('teacher:create_room', data);
  }, []);

  const startGame = useCallback((code: string) => {
    socketRef.current?.emit('teacher:start_game', { code });
  }, []);

  const endGame = useCallback((code: string) => {
    socketRef.current?.emit('teacher:end_game', { code });
  }, []);

  const joinRoom = useCallback((code: string, teamName: string, avatar?: string) => {
    socketRef.current?.emit('team:join', { code, teamName, avatar });
  }, []);

  const submitAnswers = useCallback((code: string, answers: Array<{ questionIndex: number; answer: string; timeTakenMs: number }>) => {
    socketRef.current?.emit('team:submit_answers', { code, answers });
  }, []);

  const sendAnswerProgress = useCallback((code: string, questionIndex: number, answer: string, timeTakenMs: number) => {
    socketRef.current?.emit('team:answer_progress', { code, questionIndex, answer, timeTakenMs });
  }, []);

  return {
    createRoom,
    startGame,
    endGame,
    joinRoom,
    submitAnswers,
    sendAnswerProgress,
    socket: socketRef,
  };
}
